import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface UiState {
  paletteOpen: boolean;
  uploadOpen: boolean;
  openPalette: () => void;
  closePalette: () => void;
  openUpload: () => void;
  closeUpload: () => void;
}

const UiContext = createContext<UiState | null>(null);

/**
 * App-wide UI chrome state: the ⌘K command palette and the upload dialog
 * can be opened from anywhere (header, docs pages, empty states), so their
 * open/closed state lives here rather than in any one page.
 */
export function UiProvider({ children }: { children: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const openUpload = useCallback(() => setUploadOpen(true), []);
  const closeUpload = useCallback(() => setUploadOpen(false), []);

  // Global ⌘K / Ctrl+K shortcut. `keydown` on window so it works with any
  // focus target; the palette itself stops propagation while open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const value = useMemo(
    () => ({ paletteOpen, uploadOpen, openPalette, closePalette, openUpload, closeUpload }),
    [paletteOpen, uploadOpen, openPalette, closePalette, openUpload, closeUpload],
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

export function useUi(): UiState {
  const context = useContext(UiContext);
  if (!context) throw new Error("useUi must be used within a UiProvider");
  return context;
}
